'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import { usePathname } from 'next/navigation';
import { Upload, Loader2, Play, Pause, Download, Captions, Zap, Check, RefreshCw, Type, Trash2, Plus, SkipBack, SkipForward, Volume2, Search, Eye, EyeOff, Edit3, Clock } from 'lucide-react';
import { useSpeechToText } from '@/lib/hooks/useSpeechToText';

// ─── Types ────────────────────────────────────────────────────────────────────

type InAnimation = 'pop-in-short' | 'pop-in-long' | 'transform-width' | 'slide-in-up' | 'none';
type OutAnimation = 'pop-out-short' | 'pop-out-long' | 'motion-blur' | 'slide-out-down' | 'none';

interface CaptionStyle {
  id: string;
  name: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  color: string;
  highlightColor: string;
  strokeColor: string;
  strokeWidth: number;
  shadowBlur: number;
  textTransform: 'none' | 'uppercase' | 'lowercase';
  letterSpacing: number;
  lineHeight: number;
  position: 'bottom' | 'center' | 'top';
  highlightWord: boolean;
  wordBgColor: string;
  bgColor: string;
  bgOpacity: number;
  borderRadius: number;
  padding: number;
  inAnimation: InAnimation;
  outAnimation: OutAnimation;
  isBoltMotivation?: boolean;
  boltColors?: string[];
  maxChars?: number;
  minDuration?: number;
  removeSymbols?: boolean;
  sineWave?: boolean;
  bevel?: boolean;
  dropShadow?: boolean;
  glowEffect?: boolean;
}

interface WordToken {
  word: string;
  start: number;
  end: number;
}

interface CaptionChunk {
  id: number;
  words: WordToken[];
  start: number;
  end: number;
  text: string;
  hidden?: boolean;
}

interface CustomFont {
  name: string;
  dataUrl: string;
}

// ─── Bolt Motivation Colors ───────────────────────────────────────────────────

const BOLT_COLORS = {
  orange: '#fe7c13',
  red: '#fe0100',
  green: '#56fd2e',
  purple: '#8a00fe',
  lightBlue: '#00d2fd',
  yellow: '#f5fd00',
};

const BOLT_COLOR_MAP: Record<string, string> = {
  go: BOLT_COLORS.orange, start: BOLT_COLORS.orange, now: BOLT_COLORS.orange,
  move: BOLT_COLORS.orange, push: BOLT_COLORS.orange, grind: BOLT_COLORS.orange,
  hustle: BOLT_COLORS.orange, fire: BOLT_COLORS.orange, power: BOLT_COLORS.orange,
  energy: BOLT_COLORS.orange, fast: BOLT_COLORS.orange, speed: BOLT_COLORS.orange,
  stop: BOLT_COLORS.red, fail: BOLT_COLORS.red, lose: BOLT_COLORS.red,
  never: BOLT_COLORS.red, wrong: BOLT_COLORS.red, bad: BOLT_COLORS.red,
  quit: BOLT_COLORS.red, fear: BOLT_COLORS.red, weak: BOLT_COLORS.red,
  broke: BOLT_COLORS.red, pain: BOLT_COLORS.red, hard: BOLT_COLORS.red,
  win: BOLT_COLORS.green, success: BOLT_COLORS.green, grow: BOLT_COLORS.green,
  rich: BOLT_COLORS.green, money: BOLT_COLORS.green, profit: BOLT_COLORS.green,
  gain: BOLT_COLORS.green, build: BOLT_COLORS.green, earn: BOLT_COLORS.green,
  achieve: BOLT_COLORS.green, yes: BOLT_COLORS.green, free: BOLT_COLORS.green,
  mind: BOLT_COLORS.purple, think: BOLT_COLORS.purple, know: BOLT_COLORS.purple,
  truth: BOLT_COLORS.purple, real: BOLT_COLORS.purple, deep: BOLT_COLORS.purple,
  soul: BOLT_COLORS.purple, life: BOLT_COLORS.purple, vision: BOLT_COLORS.purple,
  this: BOLT_COLORS.lightBlue, that: BOLT_COLORS.lightBlue, here: BOLT_COLORS.lightBlue,
  see: BOLT_COLORS.lightBlue, look: BOLT_COLORS.lightBlue, watch: BOLT_COLORS.lightBlue,
  best: BOLT_COLORS.yellow, top: BOLT_COLORS.yellow, only: BOLT_COLORS.yellow,
  every: BOLT_COLORS.yellow, must: BOLT_COLORS.yellow, need: BOLT_COLORS.yellow,
  key: BOLT_COLORS.yellow, secret: BOLT_COLORS.yellow, one: BOLT_COLORS.yellow,
};

function getBoltWordColor(word: string): string | null {
  const clean = word.toLowerCase().replace(/[^a-z]/g, '');
  return BOLT_COLOR_MAP[clean] ?? null;
}

// ─── Presets ──────────────────────────────────────────────────────────────────

const PRESETS: CaptionStyle[] = [
  {
    id: 'bolt-motivation',
    name: 'Bolt Motivation',
    fontFamily: '"LEMON MILK", Impact, Arial Black, sans-serif',
    fontSize: 48, fontWeight: '900', color: '#ffffff',
    highlightColor: '#fe7c13', strokeColor: '#000000', strokeWidth: 0, shadowBlur: 16,
    textTransform: 'uppercase', letterSpacing: 1, lineHeight: 1.0, position: 'bottom',
    highlightWord: true, wordBgColor: 'transparent', bgColor: '#000000', bgOpacity: 0,
    borderRadius: 0, padding: 8,
    inAnimation: 'pop-in-short', outAnimation: 'pop-out-short',
    isBoltMotivation: true, boltColors: Object.values(BOLT_COLORS),
    maxChars: 15, minDuration: 1.2, removeSymbols: true,
    sineWave: true, bevel: true, dropShadow: true, glowEffect: true,
  },
  {
    id: 'default',
    name: 'DEFAULT',
    fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 40, fontWeight: '700', color: '#ffffff',
    highlightColor: '#ffffff', strokeColor: '#000000', strokeWidth: 3, shadowBlur: 8,
    textTransform: 'none', letterSpacing: 0, lineHeight: 1.2, position: 'bottom',
    highlightWord: false, wordBgColor: '#000000', bgColor: '#000000', bgOpacity: 50, borderRadius: 4, padding: 10,
    inAnimation: 'none', outAnimation: 'none',
  },
  {
    id: 'podcast',
    name: 'PODCAST',
    fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 38, fontWeight: '600', color: '#ffffff',
    highlightColor: '#22d3ee', strokeColor: '#000000', strokeWidth: 2, shadowBlur: 10,
    textTransform: 'none', letterSpacing: 0, lineHeight: 1.3, position: 'bottom',
    highlightWord: true, wordBgColor: '#22d3ee', bgColor: '#000000', bgOpacity: 60, borderRadius: 8, padding: 12,
    inAnimation: 'pop-in-long', outAnimation: 'pop-out-long',
  },
  {
    id: 'mrbeast',
    name: 'MrBeast',
    fontFamily: 'Arial Black, Impact, sans-serif', fontSize: 64, fontWeight: '900', color: '#ffffff',
    highlightColor: '#ef4444', strokeColor: '#000000', strokeWidth: 6, shadowBlur: 12,
    textTransform: 'uppercase', letterSpacing: 1, lineHeight: 1.0, position: 'bottom',
    highlightWord: true, wordBgColor: '#ef4444', bgColor: '#000000', bgOpacity: 0, borderRadius: 6, padding: 10,
    inAnimation: 'pop-in-short', outAnimation: 'pop-out-short',
  },
  {
    id: 'hormozi',
    name: 'Hormozi',
    fontFamily: 'Impact, Arial Black, sans-serif', fontSize: 52, fontWeight: '900', color: '#ffffff',
    highlightColor: '#f59e0b', strokeColor: '#000000', strokeWidth: 4, shadowBlur: 8,
    textTransform: 'uppercase', letterSpacing: 2, lineHeight: 1.1, position: 'bottom',
    highlightWord: true, wordBgColor: '#f59e0b', bgColor: '#000000', bgOpacity: 0, borderRadius: 4, padding: 8,
    inAnimation: 'transform-width', outAnimation: 'motion-blur',
  },
  {
    id: 'iman',
    name: 'Iman',
    fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 44, fontWeight: '700', color: '#ffffff',
    highlightColor: '#a78bfa', strokeColor: '#000000', strokeWidth: 3, shadowBlur: 6,
    textTransform: 'none', letterSpacing: 0, lineHeight: 1.2, position: 'bottom',
    highlightWord: false, wordBgColor: '#a78bfa', bgColor: '#000000', bgOpacity: 0, borderRadius: 4, padding: 6,
    inAnimation: 'slide-in-up', outAnimation: 'slide-out-down',
  },
];

// ─── Animation CSS ────────────────────────────────────────────────────────────

const ANIMATION_CSS = `
@keyframes popInShort {
  0% { transform: scale(0.7); opacity: 0; }
  60% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes popInLong {
  0% { transform: scale(0.5); opacity: 0; }
  40% { transform: scale(1.15); opacity: 1; }
  65% { transform: scale(0.95); }
  80% { transform: scale(1.04); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes transformWidth {
  0% { clip-path: inset(0 100% 0 0); opacity: 0; }
  10% { opacity: 1; }
  100% { clip-path: inset(0 0% 0 0); opacity: 1; }
}
@keyframes slideInUp {
  0% { transform: translateY(28px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}
@keyframes popOutShort {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.7); opacity: 0; }
}
@keyframes popOutLong {
  0% { transform: scale(1); opacity: 1; }
  30% { transform: scale(1.08); }
  100% { transform: scale(0.4); opacity: 0; }
}
@keyframes motionBlur {
  0% { transform: translateX(0) scaleX(1); opacity: 1; filter: blur(0px); }
  100% { transform: translateX(40px) scaleX(1.3); opacity: 0; filter: blur(8px); }
}
@keyframes slideOutDown {
  0% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(28px); opacity: 0; }
}
@keyframes sineFloat {
  0%   { transform: translateY(0px); }
  25%  { transform: translateY(-2px); }
  50%  { transform: translateY(0px); }
  75%  { transform: translateY(2px); }
  100% { transform: translateY(0px); }
}
@keyframes boltGlow {
  0%, 100% { text-shadow: 0 0 8px currentColor, 0 0 20px currentColor; }
  50% { text-shadow: 0 0 14px currentColor, 0 0 36px currentColor, 0 0 60px currentColor; }
}
`;

function getInAnimStyle(anim: InAnimation): React.CSSProperties {
  switch (anim) {
    case 'pop-in-short': return { animation: 'popInShort 0.22s cubic-bezier(0.34,1.56,0.64,1) both' };
    case 'pop-in-long': return { animation: 'popInLong 0.55s cubic-bezier(0.34,1.56,0.64,1) both' };
    case 'transform-width': return { animation: 'transformWidth 0.35s ease-out both' };
    case 'slide-in-up': return { animation: 'slideInUp 0.3s ease-out both' };
    default: return {};
  }
}

// ─── Build caption chunks from word tokens ────────────────────────────────────

function buildChunks(words: WordToken[], style: CaptionStyle): CaptionChunk[] {
  if (!words.length) return [];
  const maxChars = style.maxChars ?? 20;
  const chunks: CaptionChunk[] = [];
  let current: WordToken[] = [];
  let charCount = 0;

  for (const w of words) {
    const wLen = w.word.replace(/\s/g, '').length;
    if (current.length > 0 && charCount + wLen > maxChars) {
      chunks.push({
        id: chunks.length + 1,
        words: current,
        start: current[0].start,
        end: current[current.length - 1].end,
        text: current.map(t => t.word).join(' '),
      });
      current = [];
      charCount = 0;
    }
    current.push(w);
    charCount += wLen;
  }
  if (current.length) {
    chunks.push({
      id: chunks.length + 1,
      words: current,
      start: current[0].start,
      end: current[current.length - 1].end,
      text: current.map(t => t.word).join(' '),
    });
  }
  return chunks;
}

// ─── Live Caption Overlay ─────────────────────────────────────────────────────

function LiveCaptionOverlay({ chunk, style, currentTime }: {
  chunk: CaptionChunk | null;
  style: CaptionStyle;
  currentTime: number;
}) {
  const [key, setKey] = useState(0);
  const prevChunkId = useRef<number | null>(null);

  useEffect(() => {
    if (chunk && chunk.id !== prevChunkId.current) {
      prevChunkId.current = chunk.id;
      setKey(k => k + 1);
    }
  }, [chunk]);

  if (!chunk) return null;

  const animStyle = getInAnimStyle(style.inAnimation);

  return (
    <div
      key={key}
      style={{
        ...animStyle,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '3px 5px',
        padding: '0 8px',
      }}
    >
      {chunk.words.map((wt, i) => {
        const isActive = currentTime >= wt.start && currentTime <= wt.end;
        let wordColor = style.color;
        if (style.isBoltMotivation) wordColor = getBoltWordColor(wt.word) ?? '#ffffff';
        if (style.highlightWord && isActive && !style.isBoltMotivation) wordColor = style.highlightColor;

        const sineStyle: React.CSSProperties = style.sineWave
          ? { animation: `sineFloat 2.2s ease-in-out ${i * 0.15}s infinite` }
          : {};
        const glowStyle: React.CSSProperties = style.glowEffect && style.isBoltMotivation && wordColor !== '#ffffff'
          ? { animation: `boltGlow 2s ease-in-out ${i * 0.15}s infinite`, color: wordColor }
          : {};
        const bevelFilter = style.bevel
          ? 'drop-shadow(1px 1px 0px rgba(255,255,255,0.3)) drop-shadow(-1px -1px 0px rgba(0,0,0,0.6))'
          : undefined;
        const shadowFilter = style.dropShadow
          ? 'drop-shadow(2px 4px 2px rgba(0,0,0,0.5))'
          : undefined;
        const combinedFilter = [bevelFilter, shadowFilter].filter(Boolean).join(' ') || undefined;
        const textShadow = style.shadowBlur > 0
          ? `2px 2px ${style.shadowBlur}px rgba(0,0,0,0.9)`
          : undefined;

        return (
          <span
            key={i}
            className="inline-block"
            style={{
              fontFamily: style.fontFamily,
              fontSize: style.fontSize * 0.55,
              fontWeight: style.fontWeight,
              color: wordColor,
              textTransform: style.textTransform,
              letterSpacing: style.letterSpacing,
              lineHeight: style.lineHeight,
              WebkitTextStroke: style.strokeWidth > 0 ? `${style.strokeWidth * 0.5}px ${style.strokeColor}` : undefined,
              textShadow,
              filter: combinedFilter,
              backgroundColor: isActive && style.highlightWord && !style.isBoltMotivation ? `${style.wordBgColor}40` : undefined,
              padding: isActive && !style.isBoltMotivation ? '0 3px' : undefined,
              borderRadius: isActive && !style.isBoltMotivation ? style.borderRadius : undefined,
              transform: isActive && !style.isBoltMotivation ? 'scale(1.08)' : undefined,
              transition: 'color 0.1s, transform 0.1s, background-color 0.1s',
              ...sineStyle,
              ...glowStyle,
            }}
          >
            {wt.word}
          </span>
        );
      })}
    </div>
  );
}

// ─── Font Drop Zone ───────────────────────────────────────────────────────────

function FontDropZone({ onFontLoaded }: { onFontLoaded: (font: CustomFont) => void }) {
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastLoaded, setLastLoaded] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const loadFont = useCallback(async (file: File) => {
    const validExts = ['ttf', 'otf', 'woff', 'woff2'];
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!validExts.includes(ext)) return;
    setLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const fontName = file.name.replace(/\.[^.]+$/, '');
      const styleEl = document.createElement('style');
      styleEl.textContent = `@font-face { font-family: "${fontName}"; src: url("${dataUrl}"); }`;
      document.head.appendChild(styleEl);
      onFontLoaded({ name: fontName, dataUrl });
      setLastLoaded(fontName);
      setLoading(false);
    };
    reader.readAsDataURL(file);
  }, [onFontLoaded]);

  return (
    <div>
      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest block mb-2">Drop a Font</label>
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={(e) => { e.preventDefault(); dragCounter.current = 0; setIsDragging(false); const f = e.dataTransfer.files[0]; if (f) loadFont(f); }}
        onDragEnter={(e) => { e.preventDefault(); dragCounter.current++; setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); dragCounter.current--; if (dragCounter.current === 0) setIsDragging(false); }}
        onDragOver={(e) => e.preventDefault()}
        className={`border-2 border-dashed rounded-xl p-3 flex flex-col items-center gap-1.5 cursor-pointer transition-all ${isDragging ? 'border-primary/60 bg-primary/5' : 'border-border hover:border-border/80 hover:bg-secondary/30'}`}
      >
        {loading ? <Loader2 size={16} className="text-muted-foreground animate-spin" /> : <Type size={16} className="text-muted-foreground" />}
        <p className="text-[10px] text-center text-muted-foreground">
          {lastLoaded ? <span className="text-foreground font-semibold">✓ {lastLoaded}</span> : loading ? 'Loading…' : <>Drop font · TTF OTF WOFF</>}
        </p>
      </div>
      <input ref={inputRef} type="file" accept=".ttf,.otf,.woff,.woff2" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFont(f); e.target.value = ''; }} />
    </div>
  );
}

// ─── Format time ──────────────────────────────────────────────────────────────

function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.floor((s % 1) * 10);
  return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}.${ms}`;
}

function fmtTimeSRT(s: number): string {
  const h = Math.floor(s / 3600).toString().padStart(2, '0');
  const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
  const sec = Math.floor(s % 60).toString().padStart(2, '0');
  const ms = Math.floor((s % 1) * 1000).toString().padStart(3, '0');
  return `${h}:${m}:${sec},${ms}`;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CaptionEnginePage() {
  const pathname = usePathname();

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isDraggingVideo, setIsDraggingVideo] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [activePreset, setActivePreset] = useState<CaptionStyle>(PRESETS[0]);
  const [customStyle, setCustomStyle] = useState<CaptionStyle>(PRESETS[0]);
  const [customPresets, setCustomPresets] = useState<CaptionStyle[]>([]);
  const [activeTab, setActiveTab] = useState<'templates' | 'customize'>('templates');
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const [customPresetName, setCustomPresetName] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);

  const [wordTokens, setWordTokens] = useState<WordToken[]>([]);
  const [chunks, setChunks] = useState<CaptionChunk[]>([]);
  const [editingChunkId, setEditingChunkId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoDragCounter = useRef(0);
  const chunkRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const { fullResponse, isLoading: isTranscribing, error: transcribeError, transcribe } = useSpeechToText('OPEN_AI', 'whisper-1');

  const currentStyle = activeTab === 'customize' ? customStyle : activePreset;

  useEffect(() => {
    const id = 'mogur-caption-animations';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id;
      el.textContent = ANIMATION_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('mogur_custom_presets');
      if (stored) setCustomPresets(JSON.parse(stored));
    } catch {}
  }, []);

  useEffect(() => {
    if (!fullResponse) return;
    let tokens: WordToken[] = [];

    if (fullResponse.words && Array.isArray(fullResponse.words)) {
      tokens = fullResponse.words.map((w: any) => ({
        word: w.word?.trim() ?? '',
        start: w.start ?? 0,
        end: w.end ?? 0,
      })).filter((w: WordToken) => w.word.length > 0);
    } else if (fullResponse.segments && Array.isArray(fullResponse.segments)) {
      for (const seg of fullResponse.segments) {
        const segWords = (seg.text as string).trim().split(/\s+/).filter(Boolean);
        const dur = (seg.end - seg.start) / segWords.length;
        segWords.forEach((w: string, i: number) => {
          tokens.push({ word: w, start: seg.start + i * dur, end: seg.start + (i + 1) * dur });
        });
      }
    } else if (fullResponse.text) {
      const words = (fullResponse.text as string).trim().split(/\s+/).filter(Boolean);
      const dur = duration > 0 ? duration / words.length : 0.4;
      words.forEach((w: string, i: number) => {
        tokens.push({ word: w, start: i * dur, end: (i + 1) * dur });
      });
    }

    setWordTokens(tokens);
    const newChunks = buildChunks(tokens, currentStyle);
    setChunks(newChunks);
  }, [fullResponse, duration]);

  useEffect(() => {
    if (wordTokens.length > 0) {
      setChunks(buildChunks(wordTokens, currentStyle));
    }
  }, [currentStyle.maxChars]);

  const handleVideoTimeUpdate = useCallback(() => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  }, []);

  const handleVideoLoaded = useCallback(() => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  }, []);

  const handleVideoEnded = useCallback(() => setIsPlaying(false), []);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (isPlaying) { videoRef.current.pause(); setIsPlaying(false); }
    else { videoRef.current.play(); setIsPlaying(true); }
  }, [isPlaying]);

  const seekTo = useCallback((t: number) => {
    if (videoRef.current) { videoRef.current.currentTime = t; setCurrentTime(t); }
  }, []);

  const activeChunk = chunks.find(c => currentTime >= c.start && currentTime <= c.end) ?? null;

  useEffect(() => {
    if (activeChunk && chunkRefs.current[activeChunk.id]) {
      chunkRefs.current[activeChunk.id]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeChunk?.id]);

  const handleFileSelect = useCallback(async (file: File) => {
    setUploadError(null);
    const validExts = ['mp4', 'mov', 'webm', 'mp3', 'wav', 'm4a', 'ogg', 'mpeg'];
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!validExts.includes(ext)) {
      setUploadError('Please upload MP4, MOV, WebM, MP3, or WAV');
      return;
    }
    const maxBytes = 500 * 1024 * 1024;
    if (file.size > maxBytes) {
      setUploadError('File too large. Maximum size is 500 MB.');
      return;
    }
    setUploadedFile(file);
    setWordTokens([]);
    setChunks([]);
    setCurrentTime(0);
    setIsPlaying(false);

    if (videoUrl) URL.revokeObjectURL(videoUrl);
    const url = URL.createObjectURL(file);
    setVideoUrl(url);

    await transcribe(file, {
      response_format: 'verbose_json',
      timestamp_granularities: ['word'],
    });
  }, [transcribe, videoUrl]);

  const startEdit = (chunk: CaptionChunk) => {
    setEditingChunkId(chunk.id);
    setEditingText(chunk.text);
  };

  const saveEdit = (chunkId: number) => {
    setChunks(prev => prev.map(c => {
      if (c.id !== chunkId) return c;
      const newWords = editingText.trim().split(/\s+/).filter(Boolean);
      const dur = (c.end - c.start) / Math.max(newWords.length, 1);
      const updatedWords: WordToken[] = newWords.map((w, i) => ({
        word: w,
        start: c.start + i * dur,
        end: c.start + (i + 1) * dur,
      }));
      return { ...c, text: editingText.trim(), words: updatedWords };
    }));
    setEditingChunkId(null);
  };

  const toggleChunkVisibility = (chunkId: number) => {
    setChunks(prev => prev.map(c => c.id === chunkId ? { ...c, hidden: !c.hidden } : c));
  };

  const deleteChunk = (chunkId: number) => {
    setChunks(prev => prev.filter(c => c.id !== chunkId));
  };

  const handleExportSRT = useCallback(() => {
    const visible = chunks.filter(c => !c.hidden);
    const srt = visible.map((c, i) =>
      `${i + 1}\n${fmtTimeSRT(c.start)} --> ${fmtTimeSRT(c.end)}\n${c.text}\n`
    ).join('\n');
    const blob = new Blob([srt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'captions.srt';
    a.click();
    URL.revokeObjectURL(url);
  }, [chunks]);

  const handleExportVideo = useCallback(async () => {
    if (!uploadedFile || !chunks.length) return;
    setIsExporting(true);
    setExportProgress(5);
    setExportStatus('Preparing captions…');

    try {
      const visible = chunks.filter(c => !c.hidden);
      const srt = visible.map((c, i) =>
        `${i + 1}\n${fmtTimeSRT(c.start)} --> ${fmtTimeSRT(c.end)}\n${c.text}\n`
      ).join('\n');

      const stylePayload = {
        fontFamily: currentStyle.fontFamily.split(',')[0].replace(/"/g, '').trim(),
        fontSize: currentStyle.fontSize,
        fontWeight: currentStyle.fontWeight,
        color: currentStyle.color,
        strokeColor: currentStyle.strokeColor,
        strokeWidth: currentStyle.strokeWidth,
        position: currentStyle.position,
        textTransform: currentStyle.textTransform,
        shadowBlur: currentStyle.shadowBlur,
      };

      setExportProgress(15);
      setExportStatus('Uploading to render worker…');

      const formData = new FormData();
      formData.append('video', uploadedFile);
      formData.append('srt', new Blob([srt], { type: 'text/plain' }), 'captions.srt');
      formData.append('style', JSON.stringify(stylePayload));

      const res = await fetch('/api/caption-engine/export', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Export failed' }));
        throw new Error(err.error || 'Export failed');
      }

      setExportProgress(80);
      setExportStatus('Downloading exported video…');

      const blob = await res.blob();
      setExportProgress(100);
      setExportStatus('Done!');

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `captioned-${uploadedFile.name.replace(/\.[^.]+$/, '')}.mp4`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setExportStatus(`Error: ${err.message}`);
    } finally {
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
        setExportStatus('');
      }, 2000);
    }
  }, [uploadedFile, chunks, currentStyle]);

  const updateCustomStyle = (key: keyof CaptionStyle, value: unknown) => {
    setCustomStyle(prev => ({ ...prev, [key]: value }));
  };

  const handleFontLoaded = useCallback((font: CustomFont) => {
    setCustomFonts(prev => [...prev.filter(f => f.name !== font.name), font]);
  }, []);

  const saveCustomPreset = () => {
    if (!customPresetName.trim()) return;
    const newPreset: CaptionStyle = { ...customStyle, id: `custom-${Date.now()}`, name: customPresetName.trim() };
    const updated = [...customPresets, newPreset];
    setCustomPresets(updated);
    try { localStorage.setItem('mogur_custom_presets', JSON.stringify(updated)); } catch {}
    setCustomPresetName('');
    setShowSavePreset(false);
  };

  const deleteCustomPreset = (id: string) => {
    const updated = customPresets.filter(p => p.id !== id);
    setCustomPresets(updated);
    try { localStorage.setItem('mogur_custom_presets', JSON.stringify(updated)); } catch {}
  };

  const allPresets = [...PRESETS, ...customPresets];
  const allFontOptions = [
    { label: 'Impact', value: 'Impact, Arial Black, sans-serif' },
    { label: 'Arial Black', value: 'Arial Black, Impact, sans-serif' },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Georgia', value: 'Georgia, Times New Roman, serif' },
    { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
    ...customFonts.map(f => ({ label: f.name, value: `"${f.name}", sans-serif` })),
  ];

  const filteredChunks = searchQuery
    ? chunks.filter(c => c.text.toLowerCase().includes(searchQuery.toLowerCase()))
    : chunks;

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <AppLayout activePath={pathname}>
      <div className="h-screen flex flex-col bg-[#0a0a0c] overflow-hidden">

        {/* ── Top Bar ── */}
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-white/6 bg-[#0d0d10] flex-shrink-0">
          <div className="flex items-center gap-3">
            <Captions size={15} className="text-muted-foreground" />
            <span className="text-sm font-semibold text-foreground">Caption Engine</span>
            {uploadedFile && (
              <span className="text-xs text-muted-foreground truncate max-w-48">{uploadedFile.name}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {chunks.length > 0 && (
              <button
                onClick={handleExportSRT}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary border border-border rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-all"
              >
                <Download size={12} />
                SRT
              </button>
            )}
            <button
              onClick={handleExportVideo}
              disabled={!uploadedFile || !chunks.length || isExporting}
              className="flex items-center gap-2 px-4 py-1.5 bg-foreground text-background rounded-lg text-xs font-semibold hover:bg-foreground/90 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <>
                  <Loader2 size={12} className="animate-spin" />
                  {exportProgress}%
                </>
              ) : (
                <>
                  <Download size={12} />
                  Export MP4
                </>
              )}
            </button>
          </div>
        </div>

        {/* Export progress bar */}
        {isExporting && (
          <div className="flex-shrink-0">
            <div className="h-0.5 bg-secondary">
              <div
                className="h-full bg-foreground/60 transition-all duration-500"
                style={{ width: `${exportProgress}%` }}
              />
            </div>
            <div className="px-5 py-1.5 bg-secondary/30 border-b border-border">
              <p className="text-xs text-muted-foreground font-medium">{exportStatus}</p>
            </div>
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">

          {/* ── Left Panel: Templates / Customize ── */}
          <div className="w-[260px] flex-shrink-0 border-r border-white/6 bg-[#0d0d10] flex flex-col overflow-hidden">

            {/* Panel tabs */}
            <div className="flex border-b border-white/6 flex-shrink-0">
              <button
                onClick={() => setActiveTab('templates')}
                className={`flex-1 py-2.5 text-[11px] font-semibold transition-colors ${activeTab === 'templates' ? 'text-foreground border-b border-foreground/40' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Templates
              </button>
              <button
                onClick={() => setActiveTab('customize')}
                className={`flex-1 py-2.5 text-[11px] font-semibold transition-colors ${activeTab === 'customize' ? 'text-foreground border-b border-foreground/40' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Customize
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">

              {/* TEMPLATES TAB */}
              {activeTab === 'templates' && (
                <div className="p-3 space-y-1.5">
                  {/* Bolt Motivation featured */}
                  <button
                    onClick={() => { setActivePreset(PRESETS[0]); setCustomStyle(PRESETS[0]); }}
                    className={`w-full text-left px-3 py-3 rounded-xl transition-all border ${activePreset.id === 'bolt-motivation' ? 'border-white/20 bg-white/5' : 'border-white/8 bg-white/2 hover:border-white/15 hover:bg-white/4'}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <Zap size={11} className="text-[#fe7c13]" />
                        <p className="text-xs font-bold text-foreground">Bolt Motivation</p>
                      </div>
                      <span className="text-[9px] px-1.5 py-0.5 bg-white/8 text-muted-foreground rounded-full font-medium">FEATURED</span>
                    </div>
                    <div className="flex gap-1 mb-1.5">
                      {Object.values(BOLT_COLORS).map(c => (
                        <div key={c} className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} />
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground">LEMON MILK · 48px · Smart color · Glow</p>
                  </button>

                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest px-1 pt-2 pb-1">All Templates</p>

                  <div className="grid grid-cols-2 gap-1.5">
                    {allPresets.filter(p => p.id !== 'bolt-motivation').map(preset => (
                      <div key={preset.id} className="relative group">
                        <button
                          onClick={() => { setActivePreset(preset); setCustomStyle(preset); }}
                          className={`w-full h-14 rounded-xl flex items-center justify-center transition-all border relative overflow-hidden ${activePreset.id === preset.id ? 'border-white/25 bg-white/6' : 'border-white/8 bg-[#141418] hover:border-white/15'}`}
                        >
                          <span
                            style={{
                              fontFamily: preset.fontFamily,
                              fontWeight: preset.fontWeight,
                              fontSize: 13,
                              color: preset.color,
                              textTransform: preset.textTransform,
                              WebkitTextStroke: preset.strokeWidth > 0 ? `${preset.strokeWidth * 0.3}px ${preset.strokeColor}` : undefined,
                              textShadow: preset.shadowBlur > 0 ? `1px 1px ${preset.shadowBlur * 0.4}px rgba(0,0,0,0.9)` : undefined,
                            }}
                          >
                            {preset.name}
                          </span>
                          {activePreset.id === preset.id && (
                            <div className="absolute top-1 right-1 w-3.5 h-3.5 rounded-full bg-white/20 flex items-center justify-center">
                              <Check size={8} className="text-white" />
                            </div>
                          )}
                        </button>
                        {preset.id.startsWith('custom-') && (
                          <button
                            onClick={() => deleteCustomPreset(preset.id)}
                            className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 w-4 h-4 rounded-full bg-red-500/80 flex items-center justify-center transition-all"
                          >
                            <Trash2 size={8} className="text-white" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Text settings quick bar */}
                  <div className="mt-3 pt-3 border-t border-white/6">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-2">Text settings</p>
                    <div className="flex items-center gap-2">
                      <select
                        value={currentStyle.fontFamily}
                        onChange={e => { updateCustomStyle('fontFamily', e.target.value); setActiveTab('customize'); }}
                        className="flex-1 px-2 py-1.5 bg-secondary border border-border rounded text-[10px] text-foreground focus:outline-none"
                      >
                        {allFontOptions.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                      </select>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">{currentStyle.fontSize}px</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-5 h-5 rounded border border-border cursor-pointer" style={{ backgroundColor: currentStyle.color }} />
                      <div className="w-5 h-5 rounded border border-border cursor-pointer" style={{ backgroundColor: currentStyle.strokeColor }} />
                      <span className="text-[10px] text-muted-foreground">Text · Stroke</span>
                    </div>
                  </div>
                </div>
              )}

              {/* CUSTOMIZE TAB */}
              {activeTab === 'customize' && (
                <div className="p-4 space-y-4">
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest block mb-2">Base Preset</label>
                    <select
                      value={customStyle.id}
                      onChange={e => { const found = allPresets.find(p => p.id === e.target.value); if (found) setCustomStyle(found); }}
                      className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-xs text-foreground focus:outline-none"
                    >
                      {allPresets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>

                  <FontDropZone onFontLoaded={handleFontLoaded} />

                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest block mb-2">Font</label>
                    <select
                      value={customStyle.fontFamily}
                      onChange={e => updateCustomStyle('fontFamily', e.target.value)}
                      className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-xs text-foreground focus:outline-none"
                    >
                      {allFontOptions.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest block mb-2">Font Size — {customStyle.fontSize}px</label>
                    <input type="range" min={20} max={100} value={customStyle.fontSize}
                      onChange={e => updateCustomStyle('fontSize', Number(e.target.value))}
                      className="w-full accent-primary" />
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest block mb-2">Text Color</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={customStyle.color} onChange={e => updateCustomStyle('color', e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border border-border bg-transparent" />
                      <input type="text" value={customStyle.color} onChange={e => updateCustomStyle('color', e.target.value)}
                        className="flex-1 px-2 py-1.5 bg-secondary border border-border rounded text-xs text-foreground focus:outline-none" />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest block mb-2">Highlight Color</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={customStyle.highlightColor} onChange={e => updateCustomStyle('highlightColor', e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border border-border bg-transparent" />
                      <input type="text" value={customStyle.highlightColor} onChange={e => updateCustomStyle('highlightColor', e.target.value)}
                        className="flex-1 px-2 py-1.5 bg-secondary border border-border rounded text-xs text-foreground focus:outline-none" />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest block mb-2">Stroke — {customStyle.strokeWidth}px</label>
                    <input type="range" min={0} max={12} value={customStyle.strokeWidth}
                      onChange={e => updateCustomStyle('strokeWidth', Number(e.target.value))}
                      className="w-full accent-primary" />
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest block mb-2">Position</label>
                    <div className="flex gap-1">
                      {(['top', 'center', 'bottom'] as const).map(p => (
                        <button key={p} onClick={() => updateCustomStyle('position', p)}
                          className={`flex-1 py-1.5 rounded text-[10px] font-semibold capitalize transition-all ${customStyle.position === p ? 'bg-white/15 text-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}>
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest block mb-2">Case</label>
                    <div className="flex gap-1">
                      {(['none', 'uppercase', 'lowercase'] as const).map(t => (
                        <button key={t} onClick={() => updateCustomStyle('textTransform', t)}
                          className={`flex-1 py-1.5 rounded text-[10px] font-semibold transition-all ${customStyle.textTransform === t ? 'bg-white/15 text-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}>
                          {t === 'none' ? 'Normal' : t === 'uppercase' ? 'CAPS' : 'lower'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Word Highlight</label>
                    <button
                      onClick={() => updateCustomStyle('highlightWord', !customStyle.highlightWord)}
                      className={`w-10 h-5 rounded-full transition-all relative ${customStyle.highlightWord ? 'bg-white/30' : 'bg-secondary border border-border'}`}
                    >
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${customStyle.highlightWord ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Remove Symbols</label>
                    <button
                      onClick={() => updateCustomStyle('removeSymbols', !customStyle.removeSymbols)}
                      className={`w-10 h-5 rounded-full transition-all relative ${customStyle.removeSymbols ? 'bg-white/30' : 'bg-secondary border border-border'}`}
                    >
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${customStyle.removeSymbols ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </div>

                  <div className="border-t border-white/6 pt-4">
                    {!showSavePreset ? (
                      <button
                        onClick={() => setShowSavePreset(true)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-secondary border border-border rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-all"
                      >
                        <Plus size={13} />
                        Save as Custom Preset
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={customPresetName}
                          onChange={e => setCustomPresetName(e.target.value)}
                          placeholder="Preset name…"
                          className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-xs text-foreground focus:outline-none"
                          onKeyDown={e => e.key === 'Enter' && saveCustomPreset()}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button onClick={saveCustomPreset} className="flex-1 py-2 bg-foreground text-background rounded-lg text-xs font-semibold hover:bg-foreground/90 transition-all">Save</button>
                          <button onClick={() => setShowSavePreset(false)} className="flex-1 py-2 bg-secondary text-muted-foreground rounded-lg text-xs font-semibold hover:text-foreground transition-all">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Center: Video Player ── */}
          <div className="flex-1 flex flex-col items-center justify-center bg-[#080809] overflow-hidden relative">

            {!uploadedFile ? (
              /* Upload zone */
              <div
                onDrop={(e) => { e.preventDefault(); videoDragCounter.current = 0; setIsDraggingVideo(false); const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f); }}
                onDragEnter={(e) => { e.preventDefault(); videoDragCounter.current++; setIsDraggingVideo(true); }}
                onDragLeave={(e) => { e.preventDefault(); videoDragCounter.current--; if (videoDragCounter.current === 0) setIsDraggingVideo(false); }}
                onDragOver={e => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                className={`w-full h-full flex flex-col items-center justify-center gap-5 cursor-pointer transition-all ${isDraggingVideo ? 'bg-white/3' : 'hover:bg-white/1'}`}
              >
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center border-2 border-dashed transition-all ${isDraggingVideo ? 'border-white/40 bg-white/5' : 'border-white/15'}`}>
                  <Upload size={32} className="text-white/30" />
                </div>
                <div className="text-center">
                  <p className="text-base font-semibold text-foreground">Drop your video here</p>
                  <p className="text-sm text-muted-foreground mt-1">MP4, MOV, WebM · Up to 500 MB · 9:16 recommended</p>
                  <p className="text-xs text-muted-foreground/50 mt-1">Whisper transcribes up to 25 MB · larger files may need compression</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-secondary border border-border rounded-lg">
                  <Zap size={12} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Auto transcribed with OpenAI Whisper · word-level sync</span>
                </div>
                {uploadError && <p className="text-xs text-red-400">{uploadError}</p>}
                <input ref={fileInputRef} type="file" accept="video/*,audio/*,.mp4,.mov,.webm,.mp3,.wav,.m4a"
                  className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); e.target.value = ''; }} />
              </div>
            ) : (
              /* Video player with caption overlay */
              <div className="flex flex-col items-center justify-center w-full h-full gap-0">

                {/* Phone frame with video */}
                <div className="flex-1 flex items-center justify-center py-4">
                  <div className="relative" style={{ aspectRatio: '9/16', height: 'min(calc(100vh - 200px), 520px)' }}>
                    <div className="absolute inset-0 rounded-[28px] border border-white/10 overflow-hidden bg-black shadow-xl">
                      {videoUrl && (
                        <video
                          ref={videoRef}
                          src={videoUrl}
                          className="w-full h-full object-cover"
                          onTimeUpdate={handleVideoTimeUpdate}
                          onLoadedMetadata={handleVideoLoaded}
                          onEnded={handleVideoEnded}
                          onClick={togglePlay}
                          playsInline
                        />
                      )}

                      {/* Transcribing overlay */}
                      {isTranscribing && (
                        <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-3">
                          <Loader2 size={24} className="text-white/60 animate-spin" />
                          <p className="text-sm font-medium text-white/80">Transcribing…</p>
                          <p className="text-xs text-white/40">Whisper AI · word-level sync</p>
                        </div>
                      )}

                      {/* Caption overlay */}
                      {!isTranscribing && activeChunk && !activeChunk.hidden && (
                        <div
                          className={`absolute left-0 right-0 px-3 ${
                            currentStyle.position === 'bottom' ? 'bottom-12' :
                            currentStyle.position === 'top' ? 'top-12' : 'top-1/2 -translate-y-1/2'
                          }`}
                        >
                          <LiveCaptionOverlay
                            chunk={activeChunk}
                            style={currentStyle}
                            currentTime={currentTime}
                          />
                        </div>
                      )}

                      {/* Play/pause overlay */}
                      {!isPlaying && !isTranscribing && (
                        <div
                          className="absolute inset-0 flex items-center justify-center cursor-pointer"
                          onClick={togglePlay}
                        >
                          <div className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center border border-white/15">
                            <Play size={20} className="text-white ml-1" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Playback controls */}
                <div className="w-full max-w-lg px-6 pb-4 flex-shrink-0">
                  <div
                    className="w-full h-0.5 bg-white/10 rounded-full cursor-pointer mb-3 relative group"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pct = (e.clientX - rect.left) / rect.width;
                      seekTo(pct * duration);
                    }}
                  >
                    <div className="h-full bg-white/50 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-white shadow opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ left: `calc(${progressPct}% - 5px)` }}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button onClick={() => seekTo(Math.max(0, currentTime - 5))} className="text-white/40 hover:text-white/70 transition-colors">
                        <SkipBack size={15} />
                      </button>
                      <button
                        onClick={togglePlay}
                        className="w-8 h-8 rounded-full bg-white/15 border border-white/20 flex items-center justify-center hover:bg-white/25 transition-all"
                      >
                        {isPlaying ? <Pause size={14} className="text-white" /> : <Play size={14} className="text-white ml-0.5" />}
                      </button>
                      <button onClick={() => seekTo(Math.min(duration, currentTime + 5))} className="text-white/40 hover:text-white/70 transition-colors">
                        <SkipForward size={15} />
                      </button>
                      <div className="flex items-center gap-1.5">
                        <Volume2 size={12} className="text-white/30" />
                        <input
                          type="range" min={0} max={1} step={0.05} value={volume}
                          onChange={e => { const v = Number(e.target.value); setVolume(v); if (videoRef.current) videoRef.current.volume = v; }}
                          className="w-14 accent-white h-0.5"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white/40 font-mono">
                        {fmtTime(currentTime)} / {fmtTime(duration)}
                      </span>
                      <button
                        onClick={() => { setUploadedFile(null); setVideoUrl(null); setWordTokens([]); setChunks([]); }}
                        className="text-white/30 hover:text-white/60 transition-colors"
                      >
                        <RefreshCw size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Right Panel: Timeline / Transcript ── */}
          <div className="w-[300px] flex-shrink-0 border-l border-white/6 bg-[#0d0d10] flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-white/6 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-foreground tracking-wide">EDIT CAPTIONS</p>
                {chunks.length > 0 && (
                  <span className="text-[10px] text-muted-foreground">{chunks.length} segments</span>
                )}
              </div>
              <div className="relative">
                <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search words…"
                  className="w-full pl-7 pr-3 py-1.5 bg-secondary border border-border rounded-lg text-xs text-foreground focus:outline-none"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {!uploadedFile && (
                <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center">
                  <Captions size={24} className="text-white/15" />
                  <p className="text-xs text-muted-foreground">Upload a video to see your captions here</p>
                </div>
              )}

              {uploadedFile && isTranscribing && (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <Loader2 size={18} className="text-muted-foreground animate-spin" />
                  <p className="text-xs text-muted-foreground">Transcribing…</p>
                </div>
              )}

              {uploadedFile && transcribeError && !isTranscribing && (
                <div className="p-4">
                  <div className="bg-red-500/8 border border-red-500/15 rounded-xl p-3">
                    <p className="text-xs font-semibold text-red-400">Transcription failed</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{transcribeError.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Whisper limit: 25 MB. Try compressing the audio.</p>
                  </div>
                </div>
              )}

              {filteredChunks.length > 0 && (
                <div className="p-3 space-y-1.5">
                  {filteredChunks.map((chunk) => {
                    const isActive = currentTime >= chunk.start && currentTime <= chunk.end;
                    const isEditing = editingChunkId === chunk.id;

                    return (
                      <div
                        key={chunk.id}
                        ref={el => { chunkRefs.current[chunk.id] = el; }}
                        className={`rounded-xl border transition-all ${isActive ? 'border-white/20 bg-white/5' : 'border-white/6 bg-[#141418] hover:border-white/10'} ${chunk.hidden ? 'opacity-40' : ''}`}
                      >
                        {/* Chunk header */}
                        <div className="flex items-center justify-between px-3 py-1.5">
                          <button
                            onClick={() => seekTo(chunk.start)}
                            className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Clock size={9} />
                            <span className="font-mono">{fmtTime(chunk.start)}–{fmtTime(chunk.end)}</span>
                          </button>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => startEdit(chunk)}
                              className="p-1 rounded hover:bg-white/8 text-muted-foreground hover:text-foreground transition-all"
                            >
                              <Edit3 size={10} />
                            </button>
                            <button
                              onClick={() => toggleChunkVisibility(chunk.id)}
                              className="p-1 rounded hover:bg-white/8 text-muted-foreground hover:text-foreground transition-all"
                            >
                              {chunk.hidden ? <EyeOff size={10} /> : <Eye size={10} />}
                            </button>
                            <button
                              onClick={() => deleteChunk(chunk.id)}
                              className="p-1 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-all"
                            >
                              <Trash2 size={10} />
                            </button>
                          </div>
                        </div>

                        {/* Chunk text */}
                        <div className="px-3 pb-2.5">
                          {isEditing ? (
                            <div className="space-y-1.5">
                              <textarea
                                value={editingText}
                                onChange={e => setEditingText(e.target.value)}
                                rows={2}
                                className="w-full px-2 py-1.5 bg-secondary border border-border rounded-lg text-xs text-foreground focus:outline-none resize-none leading-relaxed"
                                autoFocus
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(chunk.id); } if (e.key === 'Escape') setEditingChunkId(null); }}
                              />
                              <div className="flex gap-1.5">
                                <button onClick={() => saveEdit(chunk.id)} className="flex-1 py-1 bg-white/15 text-foreground rounded text-[10px] font-semibold hover:bg-white/20 transition-all">Save</button>
                                <button onClick={() => setEditingChunkId(null)} className="flex-1 py-1 bg-secondary text-muted-foreground rounded text-[10px] font-semibold hover:text-foreground transition-all">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <p
                              className={`text-sm leading-snug cursor-pointer hover:text-foreground transition-colors ${isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
                              onClick={() => startEdit(chunk)}
                            >
                              {chunk.words.map((wt, wi) => {
                                const wActive = currentTime >= wt.start && currentTime <= wt.end;
                                return (
                                  <span key={wi} className={wActive ? 'text-white' : ''}>
                                    {wt.word}{wi < chunk.words.length - 1 ? ' ' : ''}
                                  </span>
                                );
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {uploadedFile && !isTranscribing && !transcribeError && chunks.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center">
                  <Loader2 size={18} className="text-white/15" />
                  <p className="text-xs text-muted-foreground">Waiting for transcript…</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </AppLayout>
  );
}
